import { Link } from "react-router-dom";
import { ArrowUpRight, RefreshCw, Sunrise, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Daily nudge card — the top-of-dashboard prompt.
 * Extracted from Dashboard.jsx in iter-31 refactor.
 * Localised in iter-45 — reads `nudge.type.*` and `nudge.cta.*` keys, with
 * a graceful fallback to whatever English label the backend shipped.
 */
const ACCENT_BY_TYPE = {
  bill_urgent:    "#C45B4D",
  streak_risk:    "#E09E50",
  budget_pace:    "#E09E50",
  save_available: "#86A8D8",
  goal_behind:    "#E09E50",
  optimizer:      "#437A55",
  portfolio_up:   "#437A55",
  milestone:      "#E09E50",
  recap:          "#576A5E",
};

export default function NudgeCard({ nudge, onDismiss, onRefresh, refreshing }) {
  const { t } = useTranslation();
  const accent = ACCENT_BY_TYPE[nudge.type] || ACCENT_BY_TYPE.recap;
  // Prefer localised label_key from the backend; fall back to whatever
  // English `action_label` shipped so older cached payloads still render.
  const label = nudge.label_key ? t(nudge.label_key) : t(`nudge.type.${nudge.type || "recap"}`, "Today");
  const actionLabel = nudge.action_label_key
    ? t(nudge.action_label_key)
    : (nudge.action_label || t("nudge.cta.open_dashboard"));

  return (
    <div
      className="card-flat p-5 mb-6 relative overflow-hidden animate-fadeup morning-glow shadow-soft"
      data-testid="dashboard-nudge-card"
      style={{
        borderColor: "var(--border-default)",
        borderLeft: `4px solid ${accent}`,
      }}
    >
      <div className="flex items-start gap-5">
        <div
          className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}18`, color: accent }}
        >
          <Sunrise size={20} strokeWidth={2}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="label-eyebrow" style={{ color: accent }}>
              {t("nudge.eyebrow")} · {label}
            </span>
          </div>
          <h3
            className="text-lg md:text-xl font-medium leading-snug tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}
            data-testid="nudge-headline"
          >
            {nudge.headline}
          </h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1.5 leading-relaxed">
            {nudge.subtext}
          </p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Link to={nudge.action_route} className="btn-primary text-sm" data-testid="nudge-action-btn">
              {actionLabel} <ArrowUpRight size={14}/>
            </Link>
            <button
              className="btn-ghost text-sm"
              onClick={onRefresh}
              disabled={refreshing}
              title={t("nudge.reroll_title")}
              data-testid="nudge-refresh-btn"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""}/> {refreshing ? "" : t("nudge.reroll")}
            </button>
          </div>
        </div>
        <button
          className="btn-danger-ghost -mt-1 -mr-1"
          onClick={onDismiss}
          title={t("nudge.dismiss_title")}
          data-testid="nudge-dismiss-btn"
        >
          <X size={16}/>
        </button>
      </div>
    </div>
  );
}
